import { bunja } from 'bunja';
import { atomWithMutation } from 'jotai-tanstack-query';

import { postOneWayFlipApi, putInstantFlipApi } from '@flipster/apis/v2/trade';
import type { PutInstantFlipParams } from '@flipster/apis/v2/trade/types';
import { T } from '@flipster/i18n';
import { accountBunja } from '@flipster/r230522/original/account';
import { refPositionTimes } from '@flipster/r230522/original/server-time';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { checkAxiosError } from '@flipster/utils/axios';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { getErrorMessage } from '@flipster/utils/error';

import { tradeRequestBaseBunja } from '../base-params';

export interface InstantFlipVariables {
  symbol: string;
  slot: number;
  side: PutInstantFlipParams['side'];
  price: PutInstantFlipParams['price'];
}

/**
 * 포지션 즉시 반전(flip) 액션 (구 apps/web useInstantFlipModal의 실행 로직).
 * - MultiplePositions → putInstantFlipApi + 성공 토스트 / OneWay → postOneWayFlipApi(토스트 없음)
 * - 에러: axios 에러만 스낵바, 그 외(계정 없음 포함)는 조용히 무시 — 기존 동작 보존, rethrow 없음.
 * 로딩은 호출측 per-instance(useLoading 등) 책임 — 행 단위 독립 스피너 유지.
 */
export const instantFlipActionBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { getBase } = bunja.use(tradeRequestBaseBunja);
  const { v2AccountAtom } = bunja.use(accountBunja);

  const flipMutationAtom = atomWithMutation(() => ({
    mutationKey: ['putInstantFlipApi'],
    mutationFn: async ({ symbol, slot, side, price }: InstantFlipVariables) => {
      const account = store.get(v2AccountAtom);
      if (!account) throw new Error('Unexpected missing account');

      const params = { ...getBase(), side, price, ...refPositionTimes[symbol] };
      if (account.tradeMode === 'MultiplePositions') {
        await putInstantFlipApi(symbol, slot, params);
        snackbar.success({ message: <T id='Position.r250401.Position_update_toast' /> });
      } else {
        await postOneWayFlipApi(symbol, params);
      }
    },
  }));

  const instantFlip = async (variables: InstantFlipVariables): Promise<void> => {
    try {
      await store.get(flipMutationAtom).mutateAsync(variables);
    } catch (err) {
      if (!checkAxiosError(err)) return;
      snackbar.error({ message: getErrorMessage(err) });
    }
  };

  return { instantFlip };
});
instantFlipActionBunja.debugLabel = 'instantFlipActionBunja';
